/**
 * use-unsaved-guard — registro in-memory de "trabalho não salvo".
 *
 * Rodada de hardening / G2b (PWA auto-update, 2026-07-28).
 *
 * Por quê existe:
 *   O auto-update do PWA (PwaUpdateToast) NÃO pode recarregar a página "no
 *   susto" se o usuário está no meio de algo (ex.: construtor de orçamento
 *   com alterações não salvas). Este registro deixa qualquer tela sinalizar
 *   que tem trabalho pendente; o toast lê "tem algo sujo?" e decide se
 *   atualiza sozinho ou espera.
 *
 * Como funciona:
 *   - Store de MÓDULO (in-memory), um `Set<string>` de chaves "sujas".
 *   - ZERO persistência — nada de localStorage/sessionStorage (regra do
 *     projeto). Recarregou a aba? O estado nasce limpo, que é o esperado.
 *   - Sem Context/Provider: a store é global do módulo e as telas assinam
 *     via `useSyncExternalStore`. Não precisa embrulhar a árvore em provider.
 *
 * Como uma tela liga o "sujo":
 *   ```tsx
 *   // dentro do componente da tela (ex.: construtor de orçamento):
 *   const dirty = form.formState.isDirty; // ou qualquer boolean de "mexeu e não salvou"
 *   useUnsavedGuard(dirty);
 *   ```
 *   Enquanto `dirty` for true, o PwaUpdateToast NÃO recarrega sozinho — ele
 *   mostra o toast "Nova versão — recarregar agora?" e agenda o reload pra
 *   próxima navegação, quando não estiver mais sujo. Ao desmontar a tela, a
 *   chave é liberada automaticamente.
 */

import { useEffect, useId, useSyncExternalStore } from 'react';

// Store de módulo — chaves das telas que têm trabalho não salvo.
const chavesSujas = new Set<string>();
const ouvintes = new Set<() => void>();

function notificar(): void {
  for (const ouvinte of ouvintes) ouvinte();
}

function assinar(callback: () => void): () => void {
  ouvintes.add(callback);
  return () => {
    ouvintes.delete(callback);
  };
}

function definirSujo(chave: string, sujo: boolean): void {
  const tinha = chavesSujas.has(chave);
  if (sujo && !tinha) {
    chavesSujas.add(chave);
    notificar();
  } else if (!sujo && tinha) {
    chavesSujas.delete(chave);
    notificar();
  }
}

/**
 * Getter síncrono (fora de render) — útil dentro de effects/timeouts.
 * Retorna true se QUALQUER tela sinalizou trabalho não salvo.
 */
export function hasUnsavedWork(): boolean {
  return chavesSujas.size > 0;
}

/**
 * Hook reativo — retorna true se há trabalho não salvo em qualquer tela.
 * Re-renderiza quando o estado muda. Usado pelo PwaUpdateToast.
 */
export function useHasUnsavedWork(): boolean {
  return useSyncExternalStore(
    assinar,
    () => chavesSujas.size > 0,
    () => false, // server snapshot (SSR / primeira render) — nunca sujo
  );
}

/**
 * Hook que uma tela chama pra sinalizar trabalho não salvo.
 *
 * @param dirty true enquanto a tela tem alterações não salvas.
 */
export function useUnsavedGuard(dirty: boolean): void {
  const id = useId();
  useEffect(() => {
    definirSujo(id, dirty);
    return () => definirSujo(id, false);
  }, [id, dirty]);
}
